import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import LiveBellButton from "@/components/LiveBellButton";

/**
 * AvatarLightbox — full-screen preview of a user's avatar. Tapping the
 * backdrop or the X closes it. Escape key works on desktop.
 *
 * Deliberately small: no zoom controls, no swipe. The mobile UX brief was
 * simply "let me see a bigger picture". Everything else is future scope.
 */
export default function AvatarLightbox({ open, onClose, src, handle, handleHidden }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open so pinch-zoom in Safari doesn't drag the
    // page underneath.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="avatar-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="avatar-lightbox-close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        aria-label="Close"
      >
        <X size={20} />
      </button>
      {src ? (
        <img
          src={src}
          alt={handle || "avatar"}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
          onClick={(e) => e.stopPropagation()}
          data-testid="avatar-lightbox-image"
        />
      ) : (
        <div className="w-40 h-40 sm:w-64 sm:h-64 rounded-full bg-rose-600 flex items-center justify-center text-white font-bold text-6xl">
          {handle?.[0]?.toUpperCase() || "?"}
        </div>
      )}
      {handle && (
        <div
          className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-4 px-4"
          onClick={(e) => e.stopPropagation()}
        >
          <LiveBellButton handle={handle} />
          {!handleHidden && (
            <div className="text-white/70 text-sm">@{handle}</div>
          )}
        </div>
      )}
    </div>,
    document.body
  );
}
